import type { IconName, StatusTone } from '@bookora/ui';

import { APPOINTMENT_STATUS_TONE } from '@/features/calendar/types';

/**
 * The Persian vocabulary of a patient file, in one place.
 *
 * These are *panel* labels for enum values the API returns as stable tokens
 * (`FEMALE`, `TYPE_III`, `INSTAGRAM`). Where the server sends a finished Persian
 * string — a reminder's `kindLabel`, an appointment's `statusLabel` — the panel
 * uses that string and does not translate the token itself; two vocabularies for
 * the same status is how «لغوشده» and «کنسل شده» end up on the same screen.
 *
 * An unknown token — a value the backend adds tomorrow — renders as «نامشخص»,
 * never as the raw English token. It used to fall back to the token itself
 * («ugly and truthful»), which put `WAITLISTED` in front of a receptionist who
 * cannot read it. The truth is kept for the people who can act on it: the
 * first sighting of each unknown token is logged to the console in dev.
 */

export const GENDER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  FEMALE: 'زن',
  MALE: 'مرد',
  OTHER: 'سایر',
});

/**
 * Fitzpatrick phototype. The Roman numeral is what a dermatologist writes, and
 * the description is what a new receptionist needs in order to read the chart.
 */
export const SKIN_TYPE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  TYPE_I: 'نوع I: بسیار روشن، همیشه می‌سوزد',
  TYPE_II: 'نوع II: روشن، معمولاً می‌سوزد',
  TYPE_III: 'نوع III: گندمی روشن، گاهی می‌سوزد',
  TYPE_IV: 'نوع IV: گندمی، کم می‌سوزد',
  TYPE_V: 'نوع V: سبزه، به‌ندرت می‌سوزد',
  TYPE_VI: 'نوع VI: تیره، نمی‌سوزد',
});

/** The short form, for a table cell where the sentence does not fit. */
export const SKIN_TYPE_SHORT: Readonly<Record<string, string>> = Object.freeze({
  TYPE_I: 'نوع I',
  TYPE_II: 'نوع II',
  TYPE_III: 'نوع III',
  TYPE_IV: 'نوع IV',
  TYPE_V: 'نوع V',
  TYPE_VI: 'نوع VI',
});

/**
 * What «این بیمار پیامک نمی‌خواد» actually does, said once. The form, the list
 * tag and the file header each used to describe it differently (advertising
 * only; advertising and reminders; reminders and advertising), so nobody knew
 * whether an opted-out patient still hears about her appointment. She does
 * not: `AppointmentReminderService` and the follow-up dispatcher both skip an
 * opted-out patient, and only aftercare instructions still go out.
 */
export const SMS_OPT_OUT_HINT =
  'پیامک تبلیغاتی، یادآوری نوبت و یادآوری پیگیری برای این بیمار فرستاده نمی‌شه. پیام‌های مراقبت بعد از درمان همچنان ارسال می‌شه.';

/**
 * «امتیاز باشگاه: N» on the file header — in the loyalty drawer's own terms:
 * points are earned per purchase and spent as part of a session's price.
 */
export const LOYALTY_BALANCE_HINT =
  'امتیازی که بیمار با خرید در باشگاه مشتریان جمع کرده. می‌تونه با این امتیازها بخشی از مبلغ جلسه رو پرداخت کنه.';

/** On a clickable tag chip in the patient list: what the click does. */
export const TAG_FILTER_HINT = 'فقط بیماران با این برچسب';

export const ACQUISITION_SOURCE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  INSTAGRAM: 'اینستاگرام',
  DIRECTORY: 'فهرست کلینیک‌های بوکورا',
  REFERRAL: 'معرفی بیمار دیگه',
  WALK_IN: 'مراجعهٔ حضوری',
  AD: 'تبلیغات',
  GOOGLE: 'جست‌وجوی گوگل',
  PATIENT_APP: 'اپ مراجعان',
  OTHER: 'سایر',
});

export const NOTE_VISIBILITY_LABELS: Readonly<Record<string, string>> = Object.freeze({
  ALL_STAFF: 'همهٔ کارکنان',
  DOCTORS_ONLY: 'فقط پزشکان',
  AUTHOR_ONLY: 'فقط خودم',
});

export const NOTE_VISIBILITY_HINTS: Readonly<Record<string, string>> = Object.freeze({
  ALL_STAFF: 'هر همکاری که به این پرونده دسترسی داره این یادداشت رو می‌بینه.',
  DOCTORS_ONLY:
    'فقط همکارانی که دسترسی «مشاهده یادداشت‌های محرمانه» دارن. خواندن اون در گزارش فعالیت ثبت می‌شه.',
  AUTHOR_ONLY: 'فقط خودتون می‌بینید؛ حتی مالک کلینیک به اون دسترسی نداره.',
});

/**
 * Appointment status. The overview endpoint sends the raw token here (unlike
 * the calendar endpoint, which sends `statusLabel` too), so the panel has to
 * name them.
 */
export const APPOINTMENT_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  REQUESTED: 'درخواست‌شده',
  CONFIRMED: 'تأییدشده',
  ARRIVED: 'مراجعه کرد',
  IN_PROGRESS: 'در حال انجام',
  DONE: 'انجام‌شده',
  CANCELLED: 'لغوشده',
  NO_SHOW: 'عدم مراجعه',
});

// The calendar's map, not a copy: the same status must wear the same colour
// on the patient file, the dashboard and the calendar drawer.
export const APPOINTMENT_STATUS_TONES: Readonly<Record<string, StatusTone>> =
  APPOINTMENT_STATUS_TONE;

export const SESSION_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  PLANNED: 'برنامه‌ریزی‌شده',
  COMPLETED: 'انجام‌شده',
  CANCELLED: 'لغوشده',
});

export const SESSION_STATUS_TONES: Readonly<Record<string, StatusTone>> = Object.freeze({
  PLANNED: 'progress',
  COMPLETED: 'success',
  CANCELLED: 'inert',
});

export const PACKAGE_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  ACTIVE: 'فعال',
  COMPLETED: 'تمام‌شده',
  EXPIRED: 'منقضی',
  CANCELLED: 'لغوشده',
});

export const PACKAGE_STATUS_TONES: Readonly<Record<string, StatusTone>> = Object.freeze({
  ACTIVE: 'success',
  COMPLETED: 'inert',
  EXPIRED: 'warning',
  CANCELLED: 'inert',
});

export const REMINDER_KIND_LABELS: Readonly<Record<string, string>> = Object.freeze({
  FOLLOW_UP: 'پیگیری',
  TOUCH_UP: 'ترمیم',
  NEXT_SESSION: 'جلسهٔ بعدی',
  REVIEW: 'نظرسنجی',
  AFTERCARE: 'مراقبت بعد از درمان',
});

/**
 * Reminder status. `SENT` is an automated SMS that already went out — it is
 * «done» from the desk's point of view and must not wear the overdue red.
 */
export const REMINDER_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  SCHEDULED: 'در انتظار',
  SNOOZED: 'به تعویق افتاده',
  SENT: 'پیامک ارسال شد',
  DONE: 'انجام شد',
  CANCELLED: 'لغوشده',
});

/** A patient data package, in the words of the person waiting for it. */
export const EXPORT_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  PENDING: 'در صف',
  PROCESSING: 'در حال آماده‌سازی',
  READY: 'آمادهٔ دانلود',
  FAILED: 'ناموفق',
  EXPIRED: 'منقضی شد',
});

export const EXPORT_STATUS_TONES: Readonly<Record<string, StatusTone>> = Object.freeze({
  PENDING: 'progress',
  PROCESSING: 'progress',
  READY: 'success',
  FAILED: 'danger',
  EXPIRED: 'inert',
});

export const ERASURE_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  PENDING: 'در انتظار تأیید',
  APPROVED: 'تأییدشده؛ در انتظار پاک‌سازی',
  REJECTED: 'ردشده',
  COMPLETED: 'پاک‌سازی انجام شد',
});

export const ERASURE_STATUS_TONES: Readonly<Record<string, StatusTone>> = Object.freeze({
  PENDING: 'warning',
  APPROVED: 'danger',
  REJECTED: 'inert',
  COMPLETED: 'inert',
});

/** What an unknown token reads as. Never the token itself. */
export const UNKNOWN_LABEL = 'نامشخص';

const reportedUnknown = new Set<string>();

/** `labelOf(MAP, token)` — the Persian label, or «نامشخص» when it is unknown. */
export function labelOf(
  map: Readonly<Record<string, string>>,
  token: string | null | undefined,
  fallback = '—',
): string {
  if (token === null || token === undefined || token === '') return fallback;
  const label = map[token];
  if (label !== undefined) return label;
  if (import.meta.env.DEV && !reportedUnknown.has(token)) {
    reportedUnknown.add(token);
    console.warn(`[patients] no Persian label for «${token}»`);
  }
  return UNKNOWN_LABEL;
}

export function toneOf(
  map: Readonly<Record<string, StatusTone>>,
  token: string | null | undefined,
): StatusTone {
  if (token === null || token === undefined) return 'inert';
  return map[token] ?? 'inert';
}

/** `{ value, label }` rows for a `<Select>`, in the map's declaration order. */
export function optionsOf(
  map: Readonly<Record<string, string>>,
): { value: string; label: string }[] {
  return Object.entries(map).map(([value, label]) => ({ value, label }));
}

/**
 * The «محدودشده» tag of a blacklisted patient — ONE wording and one hint on
 * every screen that shows it (critic C19: the list, the file header, the
 * patient picker, the appointment drawer and the dashboard's today card).
 * Spread it: `<StatusTag tone={BLACKLIST_TAG.tone} icon={BLACKLIST_TAG.icon}
 * hint={BLACKLIST_TAG.hint}>{BLACKLIST_TAG.label}</StatusTag>`.
 */
export const BLACKLIST_TAG: Readonly<{
  label: string;
  hint: string;
  tone: StatusTone;
  icon: IconName;
}> = Object.freeze({
  label: 'محدودشده',
  hint: 'کلینیک این بیمار رو محدود کرده؛ مثلاً به‌خاطر چند بار عدم مراجعه. قبل از قطعی کردن نوبت با مدیر کلینیک هماهنگ کنید؛ دلیلش در پرونده بیمار اومده.',
  tone: 'danger',
  icon: 'shield-off',
});

/* ── Blocked OKs in the patient file's dialogs («[why]. [what to do].») ──── */

/** «ادامه» in «فروش بسته خدمات به این بیمار», before a package is picked. */
export const SELL_PACKAGE_NO_CHOICE_REASON =
  'هنوز بسته خدماتی انتخاب نکردید. یکی از بسته‌های خدمات بالا رو انتخاب کنید.';

/** The same «ادامه» when the list of offers could not be read. */
export const PACKAGES_LOAD_FAILED_REASON =
  'فهرست بسته‌های خدمات باز نشد. «تلاش دوباره» رو در همین پنجره بزنید.';

/** The same «ادامه» when the clinic has no active offer to sell. */
export function sellPackageEmptyReason(canManage: boolean): string {
  return canManage
    ? 'بسته خدمات فعالی برای فروش تعریف نشده. اول در بخش بسته‌های خدمات یکی بسازید.'
    : 'بسته خدمات فعالی برای فروش تعریف نشده. از مدیر کلینیک بخواید یکی بسازه.';
}

/** «ادغام پرونده‌ها» before the duplicate file is chosen. */
export const MERGE_NO_SOURCE_REASON =
  'هنوز پروندهٔ تکراری انتخاب نشده. اون رو از فهرست بالا انتخاب کنید.';

/** «ادغام پرونده‌ها» when the file chosen is this one. */
export const MERGE_SAME_FILE_REASON =
  'این همون پرونده‌ای است که باز کردید. پروندهٔ دیگه رو انتخاب کنید.';

/** «ادغام پرونده‌ها» when the chosen file could not be read (another doctor's, say). */
export const MERGE_SOURCE_UNREADABLE_REASON =
  'پروندهٔ انتخاب‌شده باز نشد؛ ممکن است زیر نظر پزشک دیگری باشه. پروندهٔ دیگری انتخاب کنید.';
